import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRegisterStop, useResolveStop, getStopTypeLabel } from '@/hooks/useMachineOEE';
import { AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';

interface MachineStopDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  machineId: string;
  machineName: string;
  mode: 'register' | 'resolve';
  stopId?: string;
  currentReason?: string;
}

const STOP_TYPES = [
  { value: 'breakdown', label: 'Avería' },
  { value: 'maintenance', label: 'Mantenimiento' },
  { value: 'setup', label: 'Configuración / Cambio' },
  { value: 'material_shortage', label: 'Falta de material' },
  { value: 'quality_issue', label: 'Problema de calidad' },
  { value: 'planned', label: 'Parada planificada' },
  { value: 'unplanned', label: 'No planificada' },
  { value: 'other', label: 'Otra' },
];

export function MachineStopDialog({
  open,
  onOpenChange,
  machineId,
  machineName,
  mode,
  stopId,
  currentReason,
}: MachineStopDialogProps) {
  const [stopType, setStopType] = useState('');
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');

  const registerStop = useRegisterStop();
  const resolveStop = useResolveStop();

  const handleSubmit = async () => {
    if (mode === 'register') {
      if (!stopType || !reason) return;

      await registerStop.mutateAsync({
        machine_id: machineId,
        stop_type: stopType,
        reason,
        description: description || undefined,
      });

      // Reset form
      setStopType('');
      setReason('');
      setDescription('');
      onOpenChange(false);
    } else if (mode === 'resolve' && stopId) {
      await resolveStop.mutateAsync({
        stopId,
        resolutionNotes: resolutionNotes || undefined,
      });

      setResolutionNotes('');
      onOpenChange(false);
    }
  };

  const isSubmitting = registerStop.isPending || resolveStop.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'register' ? (
              <>
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Registrar Parada
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 text-green-500" />
                Resolver Parada
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {mode === 'register'
              ? `Registrar una parada para ${machineName}`
              : `Resolver parada de ${machineName}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {mode === 'register' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="stop-type">Tipo de Parada *</Label>
                <Select value={stopType} onValueChange={setStopType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {STOP_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">Motivo *</Label>
                <Input
                  id="reason"
                  placeholder="Ej: Fallo en el motor principal"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descripción adicional</Label>
                <Textarea
                  id="description"
                  placeholder="Detalles adicionales sobre la parada..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
              </div>
            </>
          ) : (
            <>
              {currentReason && (
                <div className="p-3 rounded-lg bg-muted">
                  <p className="text-sm text-muted-foreground">Motivo de la parada:</p>
                  <p className="font-medium">{currentReason}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="resolution">Notas de resolución</Label>
                <Textarea
                  id="resolution"
                  placeholder="Describir la solución aplicada..."
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || (mode === 'register' && (!stopType || !reason))}
            className={mode === 'resolve' ? 'bg-green-600 hover:bg-green-700' : ''}
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {mode === 'register' ? 'Registrar Parada' : 'Resolver Parada'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MachineStopDialog;
